import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ShowPerformance } from '../models/showperformance.model';
import { HttpClient } from '@angular/common/http';
import { APIResult } from '../models/APIResult.model';
import { environment } from '../environments/environment';
import { Observable } from 'rxjs';
import { ShowService } from '../app/show.service';
import { PerformanceEditDTO } from '../app/performances/performance-crew-planning-dto.model';

@Injectable({
    providedIn: 'root'
})
export class PerformanceService {

    private readonly http = inject(HttpClient);
    private readonly showService: ShowService = inject(ShowService);

    constructor(private api: ApiService) {
    }

    public async getAll(): Promise<ShowPerformance[]> {
        const showId = await this.showService.getActiveShowId();
        return await this.api.getAll<ShowPerformance>(`performances?showId=${ showId }`);
    }

    public async getAllPublished(): Promise<ShowPerformance[]> {
        const allPerformances = await this.getAll();
        return allPerformances.filter(p => p.status === 'PUBLISHED');
    }

    public async getAllForShow(showId: number, fromDate?: string, untilDate?: string): Promise<ShowPerformance[]> {
        let url = `performances?showId=${showId}`;
        if (fromDate && untilDate) {
            url += `&fromDate=${fromDate}&untilDate=${untilDate}`;
        }
        return await this.api.getAll<ShowPerformance>(url);
    }

    public async getAllPublishedForShow(showId: number, fromDate?: string, untilDate?: string): Promise<ShowPerformance[]> {
        const allPerformances = await this.getAllForShow(showId, fromDate, untilDate);
        return allPerformances.filter(p => p.status === 'PUBLISHED');
    }

    public async getNext(): Promise<ShowPerformance> {
        const currentShowId = await this.showService.getActiveShowId();
        const url = `${ environment.apiUrl }/performances/next?showId=${ currentShowId }`;

        // Auth headers come from AuthInterceptor — see P0.6 note in api.service.ts.
        const result = await this.handleRequest<APIResult<ShowPerformance>>(
            this.http.get<APIResult<ShowPerformance>>(url)
        );
        return result.data[0];
    }

    public async getById(performanceId: number): Promise<ShowPerformance> {
        return await this.api.getById<ShowPerformance>(
            `performances`,
            performanceId
        );
    }

    public isToday(performance: ShowPerformance): boolean {
        if (!performance?.datetime) {
            return false;
        }
        // Maak een nieuw Date-object voor "vandaag"
        const today = new Date();

        // Maak een Date-object van performance.datetime
        const performanceDate = new Date(performance.datetime);

        // Vergelijk alleen de "jaar, maand, dag" delen
        return (
            today.getFullYear() === performanceDate.getFullYear() &&            today.getMonth() === performanceDate.getMonth() &&            today.getDate() === performanceDate.getDate()
        );
    }

    public async getPerformanceForToday(): Promise<any> {

        const todayDate = new Date();
        const todayDateString = todayDate.toISOString().split('T')[0];
        const currentShowId = await this.showService.getActiveShowId();

        try {
            const response = await this.http
                .get<{
                    data: any[];
                }>(`${ environment.apiUrl }/performances/getPerformanceByDate?date=${ todayDateString }&showId=${ currentShowId }`)
                .toPromise();
            return response.data[0];
        } catch (error) {
            console.error(error);
        }
        return null;

    }

    public async getSpecific(start: number, amount: number) {
        const result = await this.api.getSpecific<ShowPerformance>(
            `performances`,
            start,
            amount
        );
        const performanceObjects: ShowPerformance[] = [];
        result.map(performance => {
            performanceObjects.push(new ShowPerformance().createFromJson(performance));
        });
        return performanceObjects;
    }

    public async updateNote(performance: ShowPerformance, newNote: string) {
        try {
            return await this.api.post<Performance>(
                `performances/${ performance.id }/update-note`,
                newNote
            );
        } catch (error) {
            throw error;
        }
    }

    public async getAllForMonthAndYear(month: number, year: number): Promise<any> {
        const currentShowId = await this.showService.getActiveShowId();

        try {
            // Auth headers come from AuthInterceptor — see P0.6 note in api.service.ts.
            const response = await this.http
                .get<{
                    data: any[];
                }>(`${ environment.apiUrl }/performances/monthyear?showId=${ currentShowId }&month=${ month }&year=${ year }`)
                .toPromise();

            // Controleer of er data is
            if (response?.data) {
                // Maak een ShowPerformance object voor elk item
                return response.data.map(item => new ShowPerformance().createFromJson(item));
            }

            return []; // Return een lege array als er geen data is

        } catch (error) {
            console.error(error);
        }
    }

    public async editPerformance(performanceCrewPlanningDTO: PerformanceEditDTO): Promise<any> {
        try {
            return await this.api.put<Performance>(
                `performances/${ performanceCrewPlanningDTO.performanceId }`,
                performanceCrewPlanningDTO
            );
        } catch (error) {
            throw error;
        }
    }

    private async handleRequest<T>(request: Observable<T>): Promise<T> {
        try {
            return await request.toPromise();
        } catch (error) {

            throw new Error('An error occurred while communicating with the API.');
        }
    }
}
